/**
 * Orchestrator Agent - Tools Section
 * 
 * Section 2: Available Tools and How To Use Them
 * 
 * Structure:
 * 1. Tools Preamble (includes critical rules, from Keywords AI: ORCHESTRATOR_TOOLS_PREAMBLE)
 * 2. Tool Descriptions (from each tool's getSystemPromptDescription())
 * 3. Tools Postamble (from Keywords AI: ORCHESTRATOR_TOOLS_POSTAMBLE)
 */

import type { ToolBase } from '../../base/toolBase';
import { PromptSectionBase } from '../../base/systemPrompt/PromptSectionBase';
import { getPromptService } from '../../../prompts';
import { ORCHESTRATOR_PROMPT_KEYS } from './variables';

// ═══════════════════════════════════════════════════════════════════════════
// TOOL CATEGORIES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tool categories for grouping in the prompt
 */
export type ToolCategory = 
    | 'direct'      // Direct tools (readSlide, etc.)
    | 'editing'     // Editing tools (writeSlide, addSlides)
    | 'search'      // Search agents (presentationLibrarySearch, imageSearch)
    | 'research'    // Research agents (alphaSense, externalFileSearch)
    | 'excel'       // Excel tools (linkedExcelEditor, getRelevantExcelFiles)
    | 'utility';    // Utility tools

/**
 * Tool categorization for the orchestrator
 */
export const TOOL_CATEGORIES: Record<string, ToolCategory> = {
    // Direct tools
    readSlide: 'direct',
    getChartPointPositions: 'direct',
    reorderOrDeleteSlides: 'direct',
    // DISABLED: artifact insertion now routes through writeSlide.
    // insertArtifacts: 'direct',
    getRelevantExcelFiles: 'direct',
    generateBlueprint: 'direct',
    
    // Editing tools
    writeSlide: 'editing',
    addSlides: 'editing',
    
    // Search agents
    currentPresentationSearch: 'search',
    presentationLibrarySearchAgent: 'search',
    imageSearchAgent: 'search',
    
    // Research agents
    alphaSenseSearchAgent: 'research',
    externalFileSearchAgent: 'research',
    
    // Excel tools
    linkedExcelEditorAgent: 'excel',
};

/**
 * Category headers for the categorized tools section
 */
export const CATEGORY_HEADERS: Record<ToolCategory, string> = {
    direct: '### Direct Tools',
    editing: '### Editing Tools',
    search: '### Search Agents',
    research: '### Research Agents',
    excel: '### Excel Tools',
    utility: '### Utility Tools',
};

/**
 * Category order for consistent display
 */
export const CATEGORY_ORDER: ToolCategory[] = ['direct', 'editing', 'search', 'research', 'excel', 'utility'];


// ═══════════════════════════════════════════════════════════════════════════
// TOOLS PREAMBLE & POSTAMBLE (from Keywords AI)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tools preamble - critical rules and introduction to available tools
 */
class OrchestratorToolsPreambleSection extends PromptSectionBase {
    protected readonly keywordsKey = ORCHESTRATOR_PROMPT_KEYS.toolsPreamble;
    protected readonly fallbackContent = `## CRITICAL RULES

### Rule #1: Creating New Slides
**When user says ANY of these phrases, you MUST use addSlides:**
- "Create a slide" / "Make a slide" / "Add a slide"
- "Create a title slide" / "Make a new slide"
- "Insert a slide" / "Build a slide"

**NEVER use writeSlide to create new slides!**
- writeSlide can ONLY modify existing slides
- It CANNOT create new slides
- Creating slides = addSlides ONLY

### Rule #1b: Layout Selection for New Slides
**When calling addSlides, you MUST select the appropriate layout:**
1. Check the "PRESENTATION STYLE OVERVIEW" reminder for available layouts
2. Each layout shows: \`"Layout Name" [id: layout_XXXXX#N]\`
3. Pass BOTH \`layoutId\` AND \`layoutName\` in each operation
4. **Positions are 1-based** (slide 1 = first slide)

### Rule #2: Always Read Before Editing — QUICK vs THOROUGH
**Before calling writeSlide, you MUST call readSlide first.**

1. Call **readSlide** to get the ReadSlideML content (stored as e.g. slide_abc123)
2. Decide the edit complexity:

**QUICK edit (text/style only — no layout changes):**
\`\`\`
readSlide → writeSlide
\`\`\`
Use writeSlide with search/replace blocks that target existing XML elements.

**THOROUGH edit (layout changes — add/remove/move/resize content):**
\`\`\`
readSlide → generateBlueprint → writeSlide
\`\`\`
Call generateBlueprint with just your intent and constraints (what must NOT change). Gemini analyzes the slide image and produces a layout diagram showing the target positions and sizes. The blueprint auto-injects into your next message as a visual reference.

**You MUST follow the blueprint layout.** The blueprint is your source of truth for positions and sizes. Your writeSlide coordinates must match the proportions shown in the blueprint — if the blueprint shows a section wider than another, your point values must reflect that.

**CRITICAL - Image Asset IDs:**
- Asset IDs like "logo_omxqz4pl" come from imageSearchAgent results
- **MANDATORY:** Extract them from results[].relevantResults.assets[].assetId
- In writeSlide search/replace blocks, reference images by their contextId attribute
- Never use placeholder IDs like "logo_1" or filenames like "microsoft_logo.png"

### Copying and Space Rules (Orchestrator Must Enforce)
- For layout changes, call generateBlueprint first — it does the spatial reasoning for you.
- If creating new shapes, match style from existing shapes on the slide (fonts, sizes, fills, spacing).

### Rule #3: Don't Over-Expand Requirements
**Keep writeSlide instructions close to user intent:**
- Describe WHAT should change, not every micro-step
- writeSlide understands the ReadSlideML format and can handle complex edits
- Your job is to provide clear search/replace blocks, not enumerate every attribute

## Available Tools

You have access to the following tools to help users with their PowerPoint presentations.
Each tool is described below with its purpose and usage guidance.`;
}

/**
 * Tools postamble - closing guidance
 */
class OrchestratorToolsPostambleSection extends PromptSectionBase {
    protected readonly keywordsKey = ORCHESTRATOR_PROMPT_KEYS.toolsPostamble;
    protected readonly fallbackContent = `## Tool Usage Summary

Remember:
- **Creating slides** → addSlides (with proper layout!)
- **Editing slides (text/style)** → readSlide → writeSlide (QUICK)
- **Editing slides (layout)** → readSlide → generateBlueprint → writeSlide (THOROUGH)
- **Detailed guardrails/examples** → promptSearch → promptGet (when the system prompt alone is insufficient)
- **Finding content in current deck** → currentPresentationSearch
- **Finding content in presentation library** → presentationLibrarySearchAgent
- **Research data** → alphaSenseSearchAgent, externalFileSearchAgent
- **Images/logos** → imageSearchAgent
  - Include context in requirements: background color, aspect ratio, placement
  - Example: "Microsoft logo for dark background, square shape for header"
  - Extract asset IDs and reference via contextId in writeSlide replace blocks

Always use the right tool for the job and follow the critical rules above.`;
}

const toolsPreambleSection = new OrchestratorToolsPreambleSection();
const toolsPostambleSection = new OrchestratorToolsPostambleSection();

/**
 * Export constants for backwards compatibility
 */
export const ORCHESTRATOR_TOOLS_PREAMBLE = toolsPreambleSection.getFallbackContent();
export const ORCHESTRATOR_TOOLS_POSTAMBLE = toolsPostambleSection.getFallbackContent();

/**
 * Get the tools preamble.
 * Uses Keywords AI if available, otherwise falls back to code.
 */
export function getToolsPreamble(useFallback?: boolean): string {
    return useFallback ? toolsPreambleSection.getFallbackContent() : toolsPreambleSection.getContent();
}

/**
 * Get the tools postamble.
 * Uses Keywords AI if available, otherwise falls back to code.
 */
export function getToolsPostamble(useFallback?: boolean): string {
    return useFallback ? toolsPostambleSection.getFallbackContent() : toolsPostambleSection.getContent();
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS SECTION BUILDER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build the tools section.
 * 
 * Structure:
 * 1. Preamble (includes critical rules, from Keywords AI)
 * 2. Tool Descriptions (from each tool's getSystemPromptDescription())
 * 3. Postamble (from Keywords AI)
 */
export function buildToolsSection(tools: ToolBase[], useFallback?: boolean): string {
    const parts: string[] = [];
    
    // 1. Preamble (includes critical rules)
    parts.push(getToolsPreamble(useFallback));
    parts.push('');
    
    // 2. Tool Descriptions (from each tool's systemPromptDescription)
    for (const tool of tools) {
        parts.push(tool.systemPromptDescription);
        parts.push('');
    }
    
    // 3. Postamble
    parts.push('---');
    parts.push('');
    parts.push(getToolsPostamble(useFallback));
    
    return parts.join('\n');
}

/**
 * Build a categorized tools section (alternative format)
 * Groups tools by category for better organization in the prompt.
 */
export function buildCategorizedToolsSection(tools: ToolBase[], useFallback?: boolean): string {
    const categorized = new Map<ToolCategory, ToolBase[]>();
    
    // Group tools by category
    for (const tool of tools) {
        const category = TOOL_CATEGORIES[tool.name] || 'utility';
        const existing = categorized.get(category) ?? [];
        existing.push(tool);
        categorized.set(category, existing);
    }
    
    const parts: string[] = [];
    
    // 1. Preamble (includes critical rules)
    parts.push(getToolsPreamble(useFallback));
    parts.push('');
    
    // 3. Categorized Tool Descriptions
    for (const category of CATEGORY_ORDER) {
        const categoryTools = categorized.get(category);
        if (categoryTools && categoryTools.length > 0) {
            parts.push(CATEGORY_HEADERS[category]);
            parts.push('');
            
            for (const tool of categoryTools) {
                parts.push(tool.systemPromptDescription);
                parts.push('');
            }
        }
    }
    
    // 4. Postamble
    parts.push('---');
    parts.push('');
    parts.push(getToolsPostamble(useFallback));
    
    return parts.join('\n');
}
